import React, { useEffect, useState } from 'react';
import { getTicketStats } from '../../../../shared/services/data/ticket.data';
import { isErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import { getReservationsDashboardUpdates } from '../../../shared/services/reservations-dashboard.data';
import { ReservationUiModel } from '../../../../shared/ui-models/reservation.ui-model';
import {
  DashboardGoal,
  LeadVertical,
  TeamPanel
} from '../../../shared/config/role-dashboard.config';
import {
  getGuestCalls,
  getHousekeepingToday,
  getTopOverdue,
  GuestCallUiModel,
  HousekeepingTodayUiModel,
  OverdueRowUiModel
} from '../../../shared/services/admin-dashboard.data';
import { ErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import { cn } from '../../../../utils/cn';
import { CHIP } from './dashboard-ui';
import { GoalsRow, GoalValues, loadGoalValues } from './goals-row.component';
import { LeadCounts, LeadsWidget } from './leads-widget.component';
import { TicketsWidget } from './tickets-widget.component';
import { DashboardGreeting } from './dashboard-greeting.component';
import {
  BarRows,
  BarRowsSkeleton,
  ListSkeleton,
  Panel,
  PanelSkeleton
} from './dashboard-panel.component';
import { titleCase } from './dashboard-ui';

type TeamDashboardProps = {
  goals: DashboardGoal[];
  verticals: LeadVertical[];
  panels: TeamPanel[];
};

const useLoad = <T extends unknown>(load: () => Promise<T | ErrorInfo>) => {
  const [value, setValue] = useState<T | null | undefined>(null);
  useEffect(() => {
    let cancelled = false;
    load()
      .then((result) => !cancelled && setValue(isErrorInfo(result) ? undefined : result))
      .catch(() => !cancelled && setValue(undefined));
    return () => {
      cancelled = true;
    };
  }, [load]);
  return value;
};

const ListRows = ({
  rows
}: {
  rows: { key: string; top: string; title: string; sub?: string; end?: React.ReactNode }[];
}) => (
  <ul className="m-0 list-none p-0">
    {rows.map((r) => (
      <li
        key={r.key}
        className="flex items-center gap-3 border-t border-gray-200 py-3 first:border-t-0"
      >
        <div className="min-w-0 flex-1">
          <div className="text-[12px] tabular-nums text-neutral-300">{r.top}</div>
          <div className="truncate text-[14px] font-gilroy-medium text-neutral-500">
            {r.title}
            {r.sub && <span className="font-gilroy-regular text-neutral-300"> · {r.sub}</span>}
          </div>
        </div>
        {r.end}
      </li>
    ))}
  </ul>
);

const Empty = ({ text }: { text: string }) => (
  <p className="m-0 py-6 text-center text-[14px] text-neutral-300">{text}</p>
);

const OverduePanel = () => {
  const rows = useLoad<OverdueRowUiModel[]>(getTopOverdue);
  return (
    <Panel title="Overdue payments">
      {rows === null ? (
        <PanelSkeleton />
      ) : !rows?.length ? (
        <Empty text={rows ? 'Nothing overdue' : 'Could not load'} />
      ) : (
        <ListRows
          rows={rows.map((r) => ({
            key: String(r.id),
            top: `${r.daysOverdue}d late · ${r.reference}`,
            title: r.guestName,
            end: (
              <span className="text-[14px] font-gilroy-bold tabular-nums text-neutral-500">
                EGP {Math.round(r.remainingEgp).toLocaleString()}
              </span>
            )
          }))}
        />
      )}
    </Panel>
  );
};

const HOUSEKEEPING_STYLE: Record<string, string> = {
  new: 'bg-secondary-100 text-secondary-800',
  in_progress: 'bg-creams-600 text-creams-900',
  done: 'bg-primary-50 text-primary-700'
};

const HousekeepingPanel = () => {
  const rows = useLoad<HousekeepingTodayUiModel[]>(getHousekeepingToday);
  return (
    <Panel
      title="Housekeeping today"
      aside={
        rows ? `${rows.filter((r) => r.status === 'done').length}/${rows.length} done` : undefined
      }
    >
      {rows === null ? (
        <PanelSkeleton />
      ) : !rows?.length ? (
        <Empty text={rows ? 'No orders today' : 'Could not load'} />
      ) : (
        <ListRows
          rows={rows.slice(0, 8).map((r) => ({
            key: r.id,
            top: r.assignee || 'Unassigned',
            title: r.unit,
            sub: r.project,
            end: (
              <span
                className={cn(
                  CHIP,
                  HOUSEKEEPING_STYLE[r.status] || 'bg-neutral-50 text-neutral-400'
                )}
              >
                {titleCase(r.status)}
              </span>
            )
          }))}
        />
      )}
    </Panel>
  );
};

const OUTCOME_STYLE: Record<string, string> = {
  'all-good': 'bg-primary-50 text-primary-700',
  'no-answer': 'bg-creams-600 text-creams-900',
  'issue-reported': 'bg-secondary-100 text-secondary-800'
};

const GuestCallsPanel = () => {
  const calls = useLoad<GuestCallUiModel[]>(getGuestCalls);
  return (
    <Panel
      title="Guest calls"
      aside={calls ? `${calls.filter((c) => c.outcome).length}/${calls.length}` : undefined}
    >
      {calls === null ? (
        <PanelSkeleton />
      ) : !calls?.length ? (
        <Empty text={calls ? 'No calls queued' : 'Could not load'} />
      ) : (
        <ListRows
          rows={calls.slice(0, 8).map((c) => ({
            key: c.id,
            top: c.checkIn
              ? new Date(c.checkIn < 1e12 ? c.checkIn * 1000 : c.checkIn).toLocaleDateString(
                  'en-GB',
                  { day: 'numeric', month: 'short' }
                )
              : '',
            title: c.guestName,
            sub: c.roomName,
            end: (
              <span
                className={cn(
                  CHIP,
                  (c.outcome && OUTCOME_STYLE[c.outcome]) || 'bg-neutral-50 text-neutral-400'
                )}
              >
                {c.outcome ? titleCase(c.outcome.replace(/-/g, ' ')) : 'To call'}
              </span>
            )
          }))}
        />
      )}
    </Panel>
  );
};

const ArrivalsPanel = () => {
  const [arrivals, setArrivals] = useState<ReservationUiModel[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getReservationsDashboardUpdates().then(
      (response) =>
        !cancelled && setArrivals(isErrorInfo(response) ? [] : response.upcomingCheckIns)
    );
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Panel title="Arrivals, next 3 days" aside={arrivals ? arrivals.length : undefined}>
      {!arrivals ? (
        <ListSkeleton dot />
      ) : !arrivals.length ? (
        <p className="m-0 py-6 text-center text-[14px] text-neutral-300">No arrivals</p>
      ) : (
        <ol className="m-0 list-none p-0">
          {arrivals.slice(0, 8).map((r, i, list) => (
            <li key={r.id} className="relative pb-4 pl-6 last:pb-0">
              {i < list.length - 1 && (
                <span className="absolute bottom-0 left-[4px] top-[14px] w-px bg-gray-200" />
              )}
              <span className="absolute left-0 top-[4px] h-[9px] w-[9px] rounded-full bg-primary-500" />
              <div className="text-[12px] tabular-nums text-neutral-300">
                {new Date(r.checkInDate).toLocaleDateString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short'
                })}
              </div>
              <div className="truncate text-[14px] font-gilroy-medium text-neutral-500">
                {r.primaryGuestName}
                <span className="font-gilroy-regular text-neutral-300">
                  {' '}
                  · {r.unitName || r.propertyName}
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
};

const TicketCategoriesPanel = () => {
  const [rows, setRows] = useState<{ label: string; value: number }[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTicketStats('week').then(
      (stats) =>
        !cancelled &&
        setRows(
          isErrorInfo(stats) ? [] : stats.categories.map((c) => ({ label: c.label, value: c.n }))
        )
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (rows && !rows.length) return null;
  return (
    <Panel title="Tickets by category, this week">
      {rows ? <BarRows rows={rows.slice(0, 8)} /> : <BarRowsSkeleton />}
    </Panel>
  );
};

export const TeamDashboard = ({ goals, verticals, panels }: TeamDashboardProps) => {
  const [values, setValues] = useState<GoalValues | null>(null);
  const [leadCounts, setLeadCounts] = useState<LeadCounts | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadGoalValues(goals, verticals).then((result) => !cancelled && setValues(result));
    return () => {
      cancelled = true;
    };
  }, [goals, verticals]);

  const hasLeads = verticals.length > 0;

  return (
    <div className="font-gilroy-regular flex w-full flex-col gap-6">
      <DashboardGreeting />
      <GoalsRow goals={goals} values={values} />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-6">
          {hasLeads && <LeadsWidget verticals={verticals} onCounts={setLeadCounts} />}
          <TicketsWidget />
        </div>
        <aside className="flex flex-col gap-4">
          {panels.includes('arrivals') && <ArrivalsPanel />}
          {panels.includes('guestCalls') && <GuestCallsPanel />}
          {panels.includes('housekeeping') && <HousekeepingPanel />}
          {panels.includes('overduePayments') && <OverduePanel />}
          {hasLeads && (
            <Panel title="Pipeline">
              {leadCounts ? (
                <BarRows
                  rows={Object.entries(leadCounts).map(([status, n]) => ({
                    label: titleCase(status),
                    value: Number(n) || 0
                  }))}
                />
              ) : (
                <BarRowsSkeleton />
              )}
            </Panel>
          )}
          <TicketCategoriesPanel />
        </aside>
      </div>
    </div>
  );
};
